import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, MapPin, Search, ShieldCheck, Users } from 'lucide-react';
import {
    nextStage, roomKey, stageLabel,
    type OrCase, type RoomResource, type RoomStay, type Stage,
} from './useAscSchedule';

const DESTINATION_KIND: Partial<Record<Stage, RoomResource['resource_kind']>> = {
    preop: 'preop_bay', inor: 'or_suite', pacu: 'pacu_bay',
};

function hm(ts: string | null): string {
    return ts ? new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';
}

function teamNames(c: OrCase): string[] {
    return [c.surgeon_name, c.anesthesiologist_name, c.circulating_nurse_name, c.scrub_tech_name]
        .filter((v): v is string => !!v);
}

export function PatientScheduleRoster({
    cases, rooms, stays, onAdvance, onRelease, advancing, releasing,
}: {
    cases: OrCase[];
    rooms: RoomResource[];
    stays: RoomStay[];
    onAdvance: (c: OrCase, toStage: Stage, toRoomResourceId?: string) => void;
    onRelease: (c: OrCase, stay: RoomStay) => void;
    advancing: boolean;
    releasing: boolean;
}) {
    const [query, setQuery] = useState('');
    const [provider, setProvider] = useState('all');
    const [room, setRoom] = useState('all');
    const [stage, setStage] = useState('all');
    const [picking, setPicking] = useState<{ c: OrCase; to: Stage } | null>(null);
    const [releaseConfirm, setReleaseConfirm] = useState<{ c: OrCase; stay: RoomStay } | null>(null);
    const [blocked, setBlocked] = useState('');

    const active = useMemo(() => cases.filter((c) =>
        (c.case_status ?? '') !== 'cancelled' && (c.schedule_status ?? '') !== 'cancelled'), [cases]);
    const providers = useMemo(() => Array.from(new Set(active.flatMap(teamNames)))
        .sort((a, b) => a.localeCompare(b)), [active]);
    const roomNames = useMemo(() => Array.from(new Set(active.map((c) => c.or_room).filter((v): v is string => !!v)))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [active]);

    const stayByEncounter = new Map(stays.filter((s) => s.encounter_id != null).map((s) => [s.encounter_id, s]));
    const roomById = new Map(rooms.map((r) => [r.resource_id, r]));
    const stayByResource = new Map(stays.map((s) => [s.room_resource_id, s]));

    const filtered = active.filter((c) => {
        const needle = query.trim().toLowerCase();
        if (needle && ![
            c.patient_name, c.procedure_name, c.case_number, c.or_room, ...teamNames(c),
        ].some((v) => v?.toLowerCase().includes(needle))) return false;
        if (provider !== 'all' && !teamNames(c).includes(provider)) return false;
        if (room !== 'all' && roomKey(c.or_room) !== roomKey(room)) return false;
        if (stage !== 'all' && (c.stage ?? 'preop') !== stage) return false;
        return true;
    });

    function openBays(to: Stage) {
        const kind = DESTINATION_KIND[to];
        if (!kind) return [];
        return rooms.filter((r) => r.resource_kind === kind && !stayByResource.has(r.resource_id));
    }

    function requestAdvance(c: OrCase) {
        setBlocked('');
        const to = nextStage(c.stage);
        if (!to) return;
        if (c.encounter_id == null) {
            setBlocked(`${c.patient_name ?? 'This patient'} must be checked in before clinical movement can begin.`);
            return;
        }
        if (to === 'discharge') {
            onAdvance(c, to);
            return;
        }
        const bays = openBays(to);
        if (bays.length === 0) {
            setBlocked(`No clean ${to === 'inor' ? 'operating room' : `${stageLabel(to)} bay`} is available. Complete turnover or release a resource first.`);
            return;
        }
        if (bays.length === 1) {
            onAdvance(c, to, bays[0].resource_id);
            return;
        }
        setPicking({ c, to });
    }

    return (
        <section className="rounded-xl border border-pt-border bg-pt-panel overflow-hidden">
            <div className="px-4 py-3 border-b border-pt-border/70 flex flex-wrap items-center gap-3">
                <div className="mr-auto">
                    <h2 className="text-sm font-semibold text-pt-text flex items-center gap-2"><Users size={16} className="text-pt-cyan" /> Patient schedule</h2>
                    <p className="text-[11px] text-pt-text-muted">One row per patient · clinical team · resource state · next safe movement</p>
                </div>
                <label className="relative min-w-[220px]">
                    <Search size={14} className="absolute left-2.5 top-2 text-pt-text-dim" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find patient, case, team…"
                        className="w-full rounded border border-pt-input-border bg-pt-darker text-pt-text text-xs pl-8 pr-3 py-1.5" />
                </label>
                <select value={provider} onChange={(e) => setProvider(e.target.value)} className="rounded border border-pt-input-border bg-pt-darker text-pt-text text-xs px-2.5 py-1.5">
                    <option value="all">All clinical staff</option>
                    {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={room} onChange={(e) => setRoom(e.target.value)} className="rounded border border-pt-input-border bg-pt-darker text-pt-text text-xs px-2.5 py-1.5">
                    <option value="all">All OR rooms</option>
                    {roomNames.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select value={stage} onChange={(e) => setStage(e.target.value)} className="rounded border border-pt-input-border bg-pt-darker text-pt-text text-xs px-2.5 py-1.5">
                    <option value="all">All stages</option>
                    <option value="preop">Pre-op</option><option value="inor">In OR</option>
                    <option value="pacu">PACU</option><option value="discharge">Discharge</option>
                </select>
            </div>

            {blocked && <div className="mx-4 mt-3 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs px-3 py-2">{blocked}</div>}

            <div className="overflow-x-auto">
                <table className="w-full min-w-[1120px] text-left">
                    <thead className="text-[10px] uppercase tracking-[0.08em] text-pt-text-dim bg-pt-darker/70">
                        <tr><th className="px-4 py-2">Time / patient</th><th className="px-3 py-2">Procedure</th><th className="px-3 py-2">Clinical team</th><th className="px-3 py-2">Resources</th><th className="px-3 py-2">Status</th><th className="px-4 py-2 text-right">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-pt-border/60">
                        {filtered.map((c) => {
                            const stay = c.encounter_id != null ? stayByEncounter.get(c.encounter_id) : undefined;
                            const assignedRoom = stay ? roomById.get(stay.room_resource_id) : undefined;
                            const next = nextStage(c.stage);
                            return (
                                <tr key={c.case_id} className="hover:bg-pt-cyan/[0.035] transition-colors align-top">
                                    <td className="px-4 py-3">
                                        <div className="text-xs text-pt-cyan font-medium">{hm(c.scheduled_start)}</div>
                                        <div className="text-sm text-pt-text font-medium">{c.patient_name ?? `Case #${c.case_id}`}</div>
                                        <div className="text-[10px] text-pt-text-dim">{c.case_number ?? `Case ${c.case_id}`}</div>
                                    </td>
                                    <td className="px-3 py-3 max-w-[240px]">
                                        <div className="text-xs text-pt-text">{c.procedure_name ?? 'Procedure TBD'}</div>
                                        <div className="text-[10px] text-pt-text-dim mt-0.5">{c.estimated_duration_minutes ?? '—'} min planned</div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="text-xs text-pt-text">{c.surgeon_name ?? 'Surgeon unassigned'}</div>
                                        <div className="text-[10px] text-pt-text-muted mt-0.5">{c.anesthesiologist_name ? `Anes · ${c.anesthesiologist_name}` : 'Anesthesia unassigned'}</div>
                                        <div className="text-[10px] text-pt-text-dim">{[c.circulating_nurse_name, c.scrub_tech_name].filter(Boolean).join(' · ') || 'Support team unassigned'}</div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="text-xs text-pt-text flex items-center gap-1"><MapPin size={12} className="text-pt-gold" /> {c.or_room ?? 'OR pending'}</div>
                                        <div className="text-[10px] mt-1 text-pt-text-muted">
                                            {assignedRoom ? `${assignedRoom.display_name} · ${stay?.status}` : c.encounter_id == null ? 'Encounter opens at check-in' : 'No active bay assignment'}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="inline-flex items-center gap-1 rounded border border-pt-cyan/30 bg-pt-cyan/10 text-pt-cyan text-[10px] uppercase tracking-wider px-2 py-1"><ShieldCheck size={11} /> {stageLabel((c.stage ?? 'preop') as Stage)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end items-center gap-2 flex-wrap">
                                            <Link to={`/cases/${c.case_id}`} className="text-[11px] text-pt-text-muted border border-pt-input-border rounded px-2.5 py-1.5 hover:text-pt-text hover:border-pt-cyan/40 flex items-center gap-1">Open <ExternalLink size={11} /></Link>
                                            {stay?.status === 'occupied' && (
                                                <button type="button" disabled={releasing} onClick={() => setReleaseConfirm({ c, stay })}
                                                    className="text-[11px] text-amber-300 border border-amber-500/40 rounded px-2.5 py-1.5 hover:bg-amber-500/10 disabled:opacity-50">Release room</button>
                                            )}
                                            {next && (
                                                <button type="button" disabled={advancing} onClick={() => requestAdvance(c)}
                                                    className="text-[11px] font-medium text-pt-darker bg-emerald-400 rounded px-2.5 py-1.5 hover:bg-emerald-300 disabled:opacity-50 flex items-center gap-1">{stageLabel(next)} <ArrowRight size={11} /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {filtered.length === 0 && <div className="px-6 py-12 text-center text-sm text-pt-text-muted">No patients match these schedule filters.</div>}

            {picking && (
                <div className="border-t border-pt-cyan/40 bg-pt-darker px-4 py-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-pt-text mr-2">Move <b>{picking.c.patient_name}</b> to:</span>
                    {openBays(picking.to).map((r) => <button key={r.resource_id} type="button" onClick={() => { onAdvance(picking.c, picking.to, r.resource_id); setPicking(null); }} className="text-xs rounded border border-pt-cyan/50 text-pt-cyan px-3 py-1.5 hover:bg-pt-cyan/10">{r.display_name}</button>)}
                    <button type="button" onClick={() => setPicking(null)} className="text-xs text-pt-text-muted px-2 py-1.5">Cancel</button>
                </div>
            )}

            {releaseConfirm && (
                <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-6">
                    <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-pt-panel p-5">
                        <h3 className="text-base font-semibold text-pt-text">Release occupied resource?</h3>
                        <p className="text-xs text-pt-text-muted mt-1">{releaseConfirm.c.patient_name} will remain in the clinical stage, but the room/bay will move into its configured turnover state. Use this for an operational exception only.</p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setReleaseConfirm(null)} className="text-xs text-pt-text-muted px-3 py-2">Keep assigned</button>
                            <button type="button" disabled={releasing} onClick={() => { onRelease(releaseConfirm.c, releaseConfirm.stay); setReleaseConfirm(null); }} className="text-xs font-medium rounded bg-amber-400 text-pt-darker px-3 py-2 disabled:opacity-50">Confirm release</button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
